import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SentGift {
  id: string;
  amount: number;
  commission_amount: number;
  message: string;
  status: string;
  created_at: string;
  currency: string;
  is_anonymous: boolean;
  purpose: string | null;
  recipient_id?: string;
  recipient: {
    name: string;
    avatar_url: string;
  } | null;
}

export const useSentGifts = (userId?: string | null) => {
  return useQuery({
    queryKey: ["sent-gifts", userId],
    queryFn: async () => {
      if (!userId) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("gifts")
        .select(`
          id,
          amount,
          commission_amount,
          message,
          status,
          created_at,
          currency,
          is_anonymous,
          purpose,
          recipient_id
        `)
        .eq("sender_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      console.log('[useSentGifts] raw rows', data);

      const rows = (data || []) as Array<any>;
      const recipientIds = Array.from(
        new Set(
          rows
            .filter((g) => g.recipient_id)
            .map((g) => g.recipient_id as string)
        )
      );

      let profiles: Record<string, { name: string; avatar_url: string }> = {};
      if (recipientIds.length) {
        const { data: usersData, error: usersError } = await (supabase as any)
          .from('public_profiles')
          .select('id, name, username, avatar_url')
          .in('id', recipientIds);
        if (usersError) throw usersError;
        for (const u of (usersData || []) as any[]) {
          const displayName = (u.name && u.name.trim()) || (u.username && u.username.trim()) || 'Unknown';
          profiles[u.id as string] = { name: displayName, avatar_url: u.avatar_url || '' };
        }
      }

      const result: SentGift[] = rows.map((g) => ({
        id: g.id,
        amount: g.amount,
        commission_amount: g.commission_amount,
        message: g.message,
        status: g.status,
        created_at: g.created_at,
        currency: g.currency,
        is_anonymous: g.is_anonymous,
        purpose: g.purpose,
        recipient_id: g.recipient_id,
        recipient: profiles[g.recipient_id] || null,
      }));
      console.log('[useSentGifts] mapped', result);

      return result;
    },
    enabled: !!userId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });
};
